import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { clearAuthData } from "../../utils/authStorage";
import "./SessionExpiredModal.css";

// Nombre del evento personalizado
const SESSION_EXPIRED_EVENT = "session-expired";

/**
 * Dispara el evento de sesión expirada desde cualquier parte de la app (ej. axios interceptor).
 * @param {string} [reason] - Razón opcional del error
 * @param {object} [custom] - Título y mensaje personalizado { titulo, mensaje }
 */
export function emitSessionExpired(reason = "", custom = null) {
  // Evitar disparar múltiples veces
  if (window.__sessionExpiredFired) return;
  window.__sessionExpiredFired = true;

  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, {
      detail: { reason, titulo: custom?.titulo, mensaje: custom?.mensaje },
    }),
  );
}

/**
 * Resetea la bandera para permitir futuros eventos (se llama al hacer logout).
 */
export function resetSessionExpiredFlag() {
  window.__sessionExpiredFired = false;
}

/**
 * Modal que se muestra cuando la sesión del usuario ha expirado o el token es inválido.
 * Bloquea toda la interfaz y ofrece un botón directo de logout.
 */
export default function SessionExpiredModal() {
  const [visible, setVisible] = useState(false);
  const [reason, setReason] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      setReason(e.detail?.reason || "");
      setCustomTitle(e.detail?.titulo || "");
      setCustomMessage(e.detail?.mensaje || "");
      setVisible(true);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, []);

  const handleLogout = useCallback(() => {
    clearAuthData();
    resetSessionExpiredFlag();
    setVisible(false);
    navigate("/");
  }, [navigate]);

  if (!visible) return null;

  const title = customTitle || "Sesión Expirada";
  const description =
    customMessage ||
    "Tu sesión ha expirado o tus credenciales ya no son válidas. Por favor, cierra sesión e inicia sesión nuevamente para continuar.";
  const icon = customTitle ? "fa-shield-alt" : "fa-user-lock";

  return (
    <div className="session-expired-overlay" role="dialog" aria-modal="true">
      <div className="session-expired-card">
        <div className="session-expired-icon-wrapper">
          <i className={`fas ${icon}`}></i>
        </div>

        <h2 className="session-expired-title">{title}</h2>

        <p className="session-expired-desc">{description}</p>

        {reason && !customMessage && (
          <p className="session-expired-reason">
            <i className="fas fa-info-circle"></i> {reason}
          </p>
        )}

        <button className="session-expired-logout-btn" onClick={handleLogout}>
          <i className="fas fa-sign-out-alt"></i>
          Cerrar sesión e iniciar de nuevo
        </button>
      </div>
    </div>
  );
}
